/**
 * NodePreviewDialog - 可複用的節點預覽對話框組件
 * 用於顯示節點的詳細內容、標題、作者等資訊
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider
} from '@mui/material';
import { convertIdToAlphanumeric } from '../../utils/idConverter';
import { parseContentToHtml } from '../../utils/markdownParser';
import { formatTimestamp } from '../../utils/timeFormatter';
import UserProfileCard from '../UserProfileCard';

/**
 * NodePreviewDialog 組件
 * @param {Object} props
 * @param {boolean} props.open - 是否打開對話框
 * @param {Function} props.onClose - 關閉對話框的回調函數
 * @param {Object} props.node - 要預覽的節點資料
 * @param {boolean} props.showUserProfile - 是否顯示用戶個人資料卡片（預設為 true）
 * @param {boolean} props.isGuestMode - 是否為訪客模式
 * @param {string} props.titlePrefix - 標題前綴（預設為空字符串）
 */
export const NodePreviewDialog = ({
  open,
  onClose,
  node,
  showUserProfile = true,
  isGuestMode = false,
  titlePrefix = ''
}) => {
  if (!node) return null;

  // 處理不同格式的節點資料
  const nodeId = node.id;
  const nodeTitle = node.title || node.data?.label || '無標題';
  const nodeContent = node.content || node.data?.source?.title || '';
  const nodeAuthor = node.author || node.data?.source?.author || '';
  const nodeAuthorId = node.authorId || node.data?.source?.authorId;
  const nodeCreatedAt = node.createdAt || node.data?.source?.createdAt;

  const currentUserId = parseInt(localStorage.getItem('userId'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {titlePrefix && <span>{titlePrefix}</span>}
          <Box
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '4px',
              padding: '2px 8px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              minWidth: '48px',
              textAlign: 'center'
            }}
          >
            {convertIdToAlphanumeric(nodeId)}
          </Box>
          <span>{nodeTitle}</span>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div
          className="node-content-container"
          dangerouslySetInnerHTML={{
            __html: parseContentToHtml(nodeContent)
          }}
        />
        {nodeAuthor && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e5e7eb' }}>
            <div>
              {showUserProfile && nodeAuthorId && nodeAuthorId !== currentUserId ? (
                <UserProfileCard
                  userId={nodeAuthorId}
                  userName={nodeAuthor + " 💬"}
                  isGuestMode={isGuestMode}
                />
              ) : (
                <span>{nodeAuthor}</span>
              )}
              {nodeCreatedAt && (
                <span> - {formatTimestamp(nodeCreatedAt)}</span>
              )}
            </div>
          </Box>
        )}
        {!nodeAuthor && nodeCreatedAt && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e5e7eb' }}>
            <Typography variant="body2" color="text.secondary">
              建立時間：{formatTimestamp(nodeCreatedAt)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="text"
          color="primary"
          sx={{
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          關閉
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodePreviewDialog;
